import {
    Avatar,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Skeleton,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { NetworkStatus, useQuery } from '@apollo/client';
import { enqueueSnackbar } from 'notistack';
import { NetProfileAuth, NetProfileAuthStatusLevel } from '@/graphql/generated/types.generated';
import { StatusLevelCountDocument } from '@/graphql/generated/status-level-count.generated';
import { useState } from 'react';
import { CheckRounded, CloseRounded } from '@mui/icons-material';
import { NetProfileAuthsDocument } from '@/graphql/generated/net-profile-auths.generated';
import ProfileAvatar from '../dashboards/profiles/ProfileAvatar';
import _ from 'lodash';
import LoadMore from '../LoadMore';
import { updateProfile } from '@/utils/profiles/update-profile';
import { getProfilesPath } from '@/utils/profiles/profiles-path';
import { useSession } from '@/providers/session-provider';
import { isBrandPricingWorkspace } from '@/utils/users-path-filter';
import DashboardSectionCard from './DashboardSectionCard';
import DashboardEmptyState from './DashboardEmptyState';

const DashboardProfilesStatus = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [dialogOpen, setDialogOpen] = useState(false);
    const { workspace } = useSession();

    const {
        loading,
        error,
        data,
        refetch: refetchStatusCount,
    } = useQuery(StatusLevelCountDocument, {
        variables: {},
        onError: (error) => enqueueSnackbar(error.message),
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    });

    const initialValue: Record<NetProfileAuthStatusLevel, number> = {
        [NetProfileAuthStatusLevel.Ok]: 0,
        [NetProfileAuthStatusLevel.Warning]: 0,
        [NetProfileAuthStatusLevel.Error]: 0,
    };
    const postToReview =
        data?.statusLevelCount.statusLevels.reduce((acc, curr) => {
            acc[curr.statusLevel] = curr.count;
            return acc;
        }, initialValue) || initialValue;

    const limitProfilesInCard = 5;

    const {
        data: profilesData,
        fetchMore: fetchMoreProfiles,
        loading: loadingProfiles,
        refetch: getProfiles,
        networkStatus,
    } = useQuery(NetProfileAuthsDocument, {
        variables: {
            statusLevels: [NetProfileAuthStatusLevel.Error],
            take: limitProfilesInCard,
        },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    });

    const refetching = loading && networkStatus === NetworkStatus.refetch;
    const initialLoad = (loading || !!error) && !refetching;

    const profilesWithErrors = profilesData?.netProfileAuths.items || [];

    const hasMoreProfiles = profilesData?.netProfileAuths.hasMore || false;

    const initialHasMore = hasMoreProfiles || profilesWithErrors.length > limitProfilesInCard;

    const emptyProfiles = !initialLoad && profilesWithErrors.length === 0;

    const fixProfiles = () => {
        setDialogOpen(true);
    };

    return (
        <>
            <DashboardSectionCard
                title={t('dashboard.profile-status.title')}
                action={{
                    label: t(
                        isBrandPricingWorkspace(workspace)
                            ? 'navigation.settings.workspace.groups-brand'
                            : 'dashboard.dev.manage-profiles',
                    ),
                    to: getProfilesPath(workspace),
                    dataId: 'dashboard-profile-status-manage',
                }}
            >
                <Stack spacing={2} maxWidth={'100%'} flex={1} maxHeight={320}>
                    {emptyProfiles ? (
                        <DashboardEmptyState
                            icon={CheckRounded}
                            tone="success"
                            title={t('dashboard.profile-status.empty.title')}
                            description={t('dashboard.profile-status.empty.support-text')}
                        />
                    ) : (
                        <>
                            <Typography variant="body2" color="text.secondary">
                                {t('dashboard.profile-status.subtitle', {
                                    errorCount: postToReview.ERROR,
                                    count: postToReview.OK + postToReview.WARNING,
                                })}
                            </Typography>
                            {/* No fixed height: it used to pin the card at 300px and drag the
                                    whole row of home cards up to that size. */}
                            <Stack spacing={2} maxWidth={'100%'} maxHeight={300} overflow={'auto'}>
                                {profilesWithErrors.slice(0, limitProfilesInCard).map((profileAuth) => (
                                    <DashboardProfile
                                        key={profileAuth.id}
                                        profileAuth={profileAuth}
                                        updateProfile={() => {
                                            const updateWindowOpened = updateProfile(
                                                profileAuth.netProfile.net.code,
                                                () => {
                                                    getProfiles({ skip: 0 });
                                                    refetchStatusCount();
                                                },
                                            );
                                            if (!updateWindowOpened) {
                                                enqueueSnackbar(t('profile-update.window-not-open'));
                                            }
                                        }}
                                    />
                                ))}
                                {initialLoad && (
                                    <Stack spacing={2}>
                                        {_.times(3, (i) => (
                                            <Stack
                                                key={i}
                                                direction={'row'}
                                                gap={1}
                                                alignItems={'center'}
                                                justifyContent={'space-between'}
                                            >
                                                <Stack direction={'row'} gap={1} alignItems={'center'}>
                                                    <Skeleton
                                                        variant="rounded"
                                                        width={theme.spacing(4)}
                                                        height={theme.spacing(4)}
                                                    />
                                                    <Stack direction={'column'}>
                                                        <Stack direction={'row'} gap={0.5}>
                                                            <Skeleton
                                                                variant="circular"
                                                                width={18}
                                                                height={18}
                                                            />
                                                            <Typography variant="body2">
                                                                <Skeleton
                                                                    variant="rounded"
                                                                    width={100}
                                                                    height={18}
                                                                />
                                                            </Typography>
                                                        </Stack>
                                                    </Stack>
                                                </Stack>
                                                <Skeleton variant="rounded" width={80} height={18} />
                                            </Stack>
                                        ))}
                                    </Stack>
                                )}

                                {initialHasMore && (
                                    <Button
                                        sx={{ mt: 'auto' }}
                                        variant="contained"
                                        color="primary"
                                        onClick={fixProfiles}
                                    >
                                        {t('str.action.resolve-issues')}
                                    </Button>
                                )}
                            </Stack>
                        </>
                    )}
                </Stack>
            </DashboardSectionCard>
            {initialHasMore && (
                <Dialog open={dialogOpen} keepMounted onClose={() => setDialogOpen(false)}>
                    <DialogTitle>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="h4">
                                {t('dashboard.profile-status.fix-dialog.title')}
                            </Typography>
                            <IconButton onClick={() => setDialogOpen(false)}>
                                <CloseRounded />
                            </IconButton>
                        </Stack>
                    </DialogTitle>
                    <DialogContent>
                        <Stack gap={2}>
                            {profilesWithErrors.map((profileAuth) => (
                                <DashboardProfile
                                    key={profileAuth.id}
                                    profileAuth={profileAuth}
                                    updateProfile={() => {
                                        const updateWindowOpened = updateProfile(
                                            profileAuth.netProfile.net.code,
                                            () => {
                                                getProfiles({ skip: 0 });
                                                refetchStatusCount();
                                            },
                                        );
                                        if (!updateWindowOpened) {
                                            enqueueSnackbar(t('profile-update.window-not-open'));
                                        }
                                    }}
                                />
                            ))}
                            {profilesData?.netProfileAuths.hasMore && !loadingProfiles && (
                                <LoadMore
                                    loadMore={() => {
                                        fetchMoreProfiles({
                                            variables: {
                                                skip: profilesWithErrors.length,
                                            },
                                        });
                                    }}
                                />
                            )}
                            {loadingProfiles && (
                                <Stack spacing={2}>
                                    {_.times(3, (i) => (
                                        <Stack
                                            key={i}
                                            direction={'row'}
                                            gap={1}
                                            alignItems={'center'}
                                            justifyContent={'space-between'}
                                        >
                                            <Stack direction={'row'} gap={1} alignItems={'center'}>
                                                <Skeleton
                                                    variant="rounded"
                                                    width={theme.spacing(4)}
                                                    height={theme.spacing(4)}
                                                />
                                                <Stack direction={'column'}>
                                                    <Stack direction={'row'} gap={0.5}>
                                                        <Skeleton variant="circular" width={18} height={18} />
                                                        <Typography variant="body2">
                                                            <Skeleton
                                                                variant="rounded"
                                                                width={100}
                                                                height={18}
                                                            />
                                                        </Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                            <Skeleton variant="rounded" width={80} height={18} />
                                        </Stack>
                                    ))}
                                </Stack>
                            )}
                        </Stack>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
};
export default DashboardProfilesStatus;

const DashboardProfile = ({
    profileAuth,
    updateProfile,
}: {
    profileAuth: NetProfileAuth;
    updateProfile: () => void;
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    return (
        <Stack
            key={profileAuth.id}
            direction={'row'}
            gap={1}
            alignItems={'center'}
            justifyContent={'space-between'}
        >
            <Stack direction={'row'} gap={1} alignItems={'center'}>
                <ProfileAvatar
                    sx={{
                        width: theme.spacing(4),
                        height: theme.spacing(4),
                        fontSize: theme.spacing(1.5),
                    }}
                    name={profileAuth.netProfile.name}
                    color={profileAuth.netProfile.net.color}
                    picture={profileAuth.netProfile.picture}
                />
                <Stack direction={'column'}>
                    <Stack direction={'row'} gap={0.5}>
                        <Avatar
                            variant="square"
                            src={`/social/${profileAuth.netProfile.net.code.toLowerCase()}.svg`}
                            sx={{ width: 18, height: 18 }}
                            alt={profileAuth.netProfile.net.name}
                        />
                        <Typography variant="body2">{profileAuth.netProfile.name}</Typography>
                    </Stack>
                    <Typography variant="caption" color="textDisabled">
                        {profileAuth.netProfile.username || ''}
                    </Typography>
                </Stack>
            </Stack>
            <Stack direction={'row'} gap={1}>
                <Button variant="text" color="primary" size="small" onClick={updateProfile}>
                    {t('profile-selector.reconnect')}
                </Button>
            </Stack>
        </Stack>
    );
};
